import { useEffect, useRef, useState } from "react";
import { callAI } from "../lib/api";
import { chatSystem, navigationTools, runTools } from "../lib/askExpert";
import { renderMD } from "../lib/markdown";
import Mascot from "./Mascot";

const SUGGESTIONS = ["What's happening on this step?", "Move to the next step", "Regenerate this"];

// "Ask Cisero" for the carousel view — a collapsible overlay drawer, not a
// layout column. It floats on top of the stage content and never shrinks
// it; collapsed it's just a small edge tab.
export default function AskExpert({ journey, stage, onAdvance, onBack, onJump, onUpdateStage }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [open, messages.length, sending]);

  const doSend = async (text) => {
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      // Send the model only what it actually said (apiContent), never the
      // "→ Jumped to X." confirmation our own code appends for display —
      // otherwise the model sees its own past turns "succeed" via plain
      // text and starts mimicking that phrase instead of calling the tool.
      // Also cap history to the last few pairs (see ChatJourney for why).
      const HISTORY_PAIRS = 4;
      const apiHistory = [...messages.slice(-HISTORY_PAIRS * 2), { role: "user", content: text }]
        .map((m) => ({ role: m.role, content: m.apiContent ?? m.content }));
      const { text: reply, toolCalls } = await callAI({
        system: chatSystem(stage, journey),
        messages: apiHistory,
        tools: navigationTools,
        maxTokens: 1000,
        temperature: 0,
      });
      const actionNote = runTools(toolCalls, { onAdvance, onBack, onJump, onUpdateStage });
      const content = [reply, actionNote].filter(Boolean).join("\n\n");
      setMessages((m) => [...m, { role: "assistant", content: content || "Done.", apiContent: reply }]);
      // The drawer is a fixed overlay (full-width under 720px) that sits on
      // top of the stage content — a real navigation just happened but is
      // invisible until the drawer closes, so close it automatically rather
      // than leaving the seller to notice and dismiss it themselves.
      const navigated = (toolCalls || []).some((c) =>
        ["advance_stage", "go_back_stage", "jump_to_stage", "regenerate_stage"].includes(c.name)
      );
      if (navigated) setOpen(false);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Connection error — try again." }]);
    }
    setSending(false);
  };

  const send = () => doSend(input.trim());
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open) {
    return (
      <button type="button" className="s-carousel-sidebar-tab" onClick={() => setOpen(true)}>
        <Mascot size={22} />
        <span>Ask Cisero</span>
      </button>
    );
  }

  return (
    <div className="s-carousel-sidebar">
      <div className="s-carousel-sidebar-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Mascot size={22} />
          <span>ASK CISERO</span>
        </div>
        <button type="button" className="s-carousel-sidebar-close" onClick={() => setOpen(false)}>×</button>
      </div>

      <div className="s-chat-scroll" style={{ flex: 1, maxWidth: "none", margin: 0 }}>
        {messages.length === 0 && (
          <div className="s-chat-suggestions" style={{ padding: 0, flexDirection: "column", alignItems: "flex-start" }}>
            {SUGGESTIONS.map((s) => (
              <button type="button" key={s} className="s-chat-pill" onClick={() => doSend(s)} disabled={sending}>{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexDirection: m.role === "user" ? "row-reverse" : "row" }}>
            {m.role === "assistant" && <Mascot size={22} style={{ flexShrink: 0, marginBottom: 2 }} />}
            <div className={`s-chat-msg ${m.role}`} dangerouslySetInnerHTML={{ __html: renderMD(m.content) }} />
          </div>
        ))}
        {sending && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <Mascot size={22} style={{ flexShrink: 0, marginBottom: 2 }} />
            <div className="s-chat-msg assistant s-chat-msg-loading">…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="s-chat-input-row" style={{ maxWidth: "none", margin: 0 }}>
        <input
          className="s-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={`Ask Cisero about ${stage.short.toLowerCase()}…`}
          disabled={sending}
        />
        <button type="button" className="s-chat-send" onClick={send} disabled={sending || !input.trim()}>Send</button>
      </div>
    </div>
  );
}
